import React, { memo } from "react";
import { FleetVehicleRow } from "services/api/fleet/vehicles/types";
import { formatCurrency, useTranslate } from "helpers";
import { ReactSVG } from "react-svg";
import { Button } from "ui/components/Button/Button";
import Tooltip from "ui/components/Tooltip/Tooltip";

interface VehicleGridCardProps {
  vehicle: FleetVehicleRow;
  onRowClick?: (id: string) => void;
}

// Vehicle grid kartica — isti dizajn kao ListingGridCard, samo bez listing
// statusa (Aktivan oglas / Pauzirano / Bez oglasa) i bez channels stack-a
// jer ovaj ekran ne barata oglasima.
const EMPTY_VALUE = "—";

const VehicleGridCard: React.FC<VehicleGridCardProps> = ({ vehicle, onRowClick }) => {
  const { t } = useTranslate();

  const handleClick = () => {
    if (onRowClick) onRowClick(vehicle.id);
  };

  const power = vehicle.vehicleType?.maximumPowerKw != null
    ? `${vehicle.vehicleType.maximumPowerKw} kW${vehicle.vehicleType.maximumPowerHp != null ? ` (${vehicle.vehicleType.maximumPowerHp} HP)` : ""}`
    : null;

  // Uvijek isti redci, istim redoslijedom — vrijednost koje nema ide kao "—".
  // Kad su se prazni redci preskakali, kartice su bile različite visine pa se
  // cijene i gumb nisu poravnavali između stupaca.
  const specs: { icon: string; value: string | null }[] = [
    { icon: "transmission", value: vehicle.vehicleType?.transmission?.name ?? null },
    { icon: "fuel", value: vehicle.vehicleType?.fuelType?.name ?? null },
    {
      icon: "speedometer",
      value: vehicle.kmTraveled != null ? `${vehicle.kmTraveled.toLocaleString("hr-HR")} km` : null,
    },
    { icon: "engine-capacity", value: power },
    {
      icon: "seat",
      value: vehicle.vehicleType?.seats ? `${vehicle.vehicleType.seats} sjedala` : null,
    },
    {
      icon: "door",
      value: vehicle.vehicleType?.numberOfDoors ? `${vehicle.vehicleType.numberOfDoors} vrata` : null,
    },
  ];

  return (
    <div
      className="relative h-full flex flex-col bg-white rounded-sm border border-gray-200 overflow-hidden cursor-pointer"
      onClick={handleClick}
    >
      {/* Slika drži omjer umjesto fiksne visine — na uskoj kartici je fiksnih 384px
          davalo izduženi portret, a na širokoj spljošteni isječak. */}
      <div className="relative aspect-[4/3] bg-gray-100">
        {vehicle.picture ? (
          <img
            src={vehicle.picture}
            alt={vehicle.name || "Vehicle"}
            className="w-full h-full object-cover object-bottom rounded-sm"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <span className="text-4xl text-gray-400 font-semibold">
              {vehicle.vehicleManufacturer?.name?.toString().charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>

      {/* Content — manji bočni razmak da na užoj kartici ostane više prostora sadržaju. */}
      <div className="p-4 flex-1 flex flex-col">
        {/* Naslov je uvijek jedan redak — dugo ime inače gura cijene niže od susjeda. */}
        <h3 className="font-semibold text-gray-900 mb-2 truncate">
          {vehicle.vehicleManufacturer?.name} – {vehicle.vehicleModel?.name}
          {(() => {
            const year = vehicle.firstRegistrationDate ? new Date(vehicle.firstRegistrationDate).getFullYear() : vehicle.vehicleModel?.year;
            return year ? ` (${year})` : "";
          })()}
        </h3>

        {/* Features */}
        <div className="mb-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {specs.map((spec) => (
              <div key={spec.icon} className="flex items-center gap-2 text-sm text-gray-400">
                <ReactSVG src={`/media/icons/${spec.icon}.svg`} className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{spec.value ?? EMPTY_VALUE}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-sm text-gray-400 col-span-2">
              <ReactSVG src="/media/icons/location.svg" className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{vehicle.location?.locationName ?? EMPTY_VALUE}</span>
            </div>
          </div>
        </div>

        {/* Cijene — isti pattern kao na detalju vozila (VehicleHeader):
            2 kolone (UKUPNA NABAVNA / PRODAJNA) + tooltip s punim breakdownom.
            `mt-auto` drži cijene i gumb na dnu kartice, poravnate kroz cijeli red. */}
        <div className="mt-auto pt-3 border-t border-gray-100 mb-3">
          <PriceSummary price={vehicle.price} t={t} />
        </div>

        <Button
          type="button"
          variant="primary"
          size="full"
          onClick={(e) => {
            e.stopPropagation();
            if (onRowClick) onRowClick(vehicle.id);
          }}
        >
          {t("admin.fleet.vehicle.grid.viewdetails")}
        </Button>
      </div>
    </div>
  );
};

// === Lokalni helper: kompaktan price summary u 2 kolone + tooltip ===
const PriceSummary: React.FC<{ price: FleetVehicleRow["price"]; t: (key: string) => string }> = ({ price, t }) => {
  const netPrice = price?.netPrice ?? 0;
  const specialTax = price?.specialTax ?? 0;
  const totalPurchase = price?.totalPurchasePrice ?? 0;
  const sellingPrice = price?.sellingPrice ?? 0;
  const margin = price?.margin ?? 0;
  const marginPct = price?.marginPercentage ?? 0;
  const totalExpenses = price?.totalExpenses ?? 0;
  const netMargin = price?.netMargin ?? margin;
  const netMarginPct = price?.netMarginPercentage;
  const isPositiveMargin = margin > 0;
  const isPositiveNetMargin = netMargin > 0;

  return (
    <div className="flex gap-3 items-end">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-amber-600 uppercase font-semibold truncate">
          Ukupna nabavna
        </div>
        <div className="text-md font-bold text-amber-600 truncate">
          {formatCurrency.format(totalPurchase)}
        </div>
      </div>
      <div className="w-px h-10 bg-gray-200" />
      <div className="flex-1 min-w-0 text-right" onClick={(e) => e.stopPropagation()}>
        {/* Tooltip iznutra renderira flex okvir, pa `text-right` s roditelja ne djeluje —
            oznaka se poravnava desno tek kroz justify-end na ovom kontejneru. */}
        <div className="text-xs text-green-600 font-semibold flex justify-end">
          <Tooltip
            decorate={
              <span className="uppercase truncate inline-block max-w-full">
                {t("admin.fleet.vehicle.detail.info.sellingsrice")}
              </span>
            }
            position="bottom-right"
            size="medium"
          >
            <div className="min-w-[520px]">
              <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Pregled cijene i zarade</div>
              <div className="flex gap-4 items-stretch">
                <div className="flex-1 min-w-[240px] flex flex-col">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">PPMV</span>
                    <span className="font-medium">{formatCurrency.format(specialTax)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Neto cijena</span>
                    <span className="font-medium">{formatCurrency.format(netPrice)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Ukupna nabavna</span>
                    <span className="font-medium">{formatCurrency.format(totalPurchase)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Prodajna cijena</span>
                    <span className="font-medium">{formatCurrency.format(sellingPrice)}</span>
                  </div>
                  <div className="border-t border-slate-600 my-2" />
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Bruto marža</span>
                    <span className={`font-bold ${isPositiveMargin ? "text-green-400" : "text-red-400"}`}>
                      {formatCurrency.format(margin)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Bruto marža %</span>
                    <span className={`font-bold ${isPositiveMargin ? "text-green-400" : "text-red-400"}`}>
                      {marginPct.toFixed(2)} %
                    </span>
                  </div>
                </div>
                <div className="w-px bg-slate-600" />
                <div className="flex-1 min-w-[240px] flex flex-col justify-end">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Troškovi</span>
                    <span className="font-medium text-rose-400">
                      -{formatCurrency.format(totalExpenses)}
                    </span>
                  </div>
                  <div className="border-t border-slate-600 my-2" />
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Neto marža</span>
                    <span className={`font-bold ${isPositiveNetMargin ? "text-green-400" : "text-red-400"}`}>
                      {formatCurrency.format(netMargin)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Neto marža %</span>
                    <span className={`font-bold ${isPositiveNetMargin ? "text-green-400" : "text-red-400"}`}>
                      {netMarginPct != null ? `${netMarginPct.toFixed(2)} %` : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Tooltip>
        </div>
        <div className="text-md font-bold text-green-600 truncate">
          {formatCurrency.format(sellingPrice)}
        </div>
      </div>
    </div>
  );
};

export default memo(VehicleGridCard);
